// src/sim/ai/utility.ts — Hunter branch utility scorer + argmax tiebreaker (D-03, Pattern 3).
//
// Utility is computed AGENT-SIDE before each tree.step() call. Each top-level
// branch in hunter.bt.mdsl is guarded with while(IsWinningBranch, id). The
// winning branch's guard passes; all others fail. This gives mistreevous a
// utility-scored top-level selector without re-parsing the tree each tick.
//
// D-42 torch-off-to-listen utility is included here (flick-torch-to-listen branch).
// D-16 investigate division is partially scored here (full wiring in Plan 10).
// D-18 retreat fear threshold is included (fear > 0.65 → retreat wins).
//
// Source: .planning/phases/04-ai/04-05-PLAN.md Task 1 action;
//         04-RESEARCH.md Pattern 3 L462-492; 04-CONTEXT.md D-03, D-16, D-18, D-42.

import type { Perception } from '../../shared/types/perception';
import type { CharacterProfile } from '../characters/character-profile';
import type { Vec2 } from '../../shared/types/intent';
import type { EventTag } from '../../shared/types/events';
import {
  P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS,
  P4_POST_DROPOFF_PATROL_BASE_PENALTY_TICKS,
  P4_LISTEN_BASE,
  P4_LISTEN_STAT_WEIGHT,
  P4_CHECKED_SPOT_PENALTY,
} from './constants';
import { computeInvestigateUtility } from './coordination/utility';
import { scoreHiderArchetypes, type HiderArchetype } from './archetypes/scorers';

/**
 * Default hear profile used when scoreHunterBranches is called without a character profile
 * (e.g. in unit tests, or before content-cache is hydrated). Stat 0.55 is the median of
 * the roster; listen baseline = 0.05 + 0.55 * 0.45 = 0.2975 + 0.3 = 0.5975 at zero fear
 * and fresh blackboard — below check (0.65), consistent with existing utility.test.ts expectations.
 */
const DEFAULT_HUNTER_HEAR_PROFILE: Pick<CharacterProfile, 'hear'> = {
  hear: { stat: 0.55, base: 10 },
};

/**
 * The 7 utility-scored branches of hunter.bt.mdsl.
 * Order matches the MDSL selector top-to-bottom (priority in ties resolved by argmaxBranch).
 */
export type HunterBranchId =
  | 'chase'
  | 'investigate-sighting'
  | 'retreat-when-afraid'
  | 'flick-torch-to-listen'
  | 'check-hiding-spot'
  | 'patrol-base'
  | 'patrol-far';

/**
 * Score all 7 hunter branches given the current Perception.
 *
 * Scoring rules:
 *   chase                — 1.0 + topSightability when any visible hider exists; 0 otherwise.
 *                          Ensures chase always beats every other branch when a hider is visible.
 *   investigate-sighting — P4_INVESTIGATE_BASE + confidence bonus when memory is non-empty.
 *   retreat-when-afraid  — 1.2 + scaled overshoot above 0.65 when fear > 0.65 (D-18).
 *   flick-torch-to-listen— 0.3 + time-since-blackboard bonus; fear penalty above 0.5 (D-42).
 *   check-hiding-spot    — fixed 0.35 base (Plan 10 refines with checkedHidingSpotIds penalty).
 *   patrol-base          — fixed 0.35 base.
 *   patrol-far           — fixed 0.30 base (always-last fallback; never -Infinity so tree can't
 *                          stall when all other branches score 0).
 */
export function scoreHunterBranches(
  p: Perception,
  duplicateCounts?: ReadonlyMap<string, number>,
  profile: Pick<CharacterProfile, 'hear'> = DEFAULT_HUNTER_HEAR_PROFILE,
  checkedFraction: number = 0,
): Record<HunterBranchId, number> {
  // --- chase ---
  // Caught hiders are excluded: a hunter parked at plinth with a just-dropped
  // hider at its feet would otherwise score chase=1+ forever and hold the
  // hunter stationary (UAT Test 2 follow-up — hunter stuck at plinth).
  const topSight = p.visible
    .filter(v => v.role === 'hider' && v.status !== 'caught')
    .reduce((m, v) => Math.max(m, v.sightability), 0);
  const chase = topSight > 0 ? 1.0 + topSight : 0;

  // --- investigate-sighting (D-16, BLOCKER-1: full formula via computeInvestigateUtility) ---
  // CR-02 fix: the caller (AIController.decide via tick.ts) passes a map of
  // per-entry duplicate-hunter counts BEFORE decide() runs argmaxBranch. Without
  // this map (e.g. in unit tests or non-hunter scorers), duplicate count defaults
  // to 0 which matches the original "fallback" behaviour.
  let investigate = 0;
  if (p.memory.length > 0) {
    // Score each entry and take the max (best blackboard entry wins the branch).
    for (const entry of p.memory) {
      const dup = duplicateCounts?.get(entry.id) ?? 0;
      const score = computeInvestigateUtility(p, entry, dup);
      if (score > investigate) investigate = score;
    }
  }

  // --- retreat-when-afraid (D-18) ---
  const retreat = p.self.fear > 0.65
    ? 1.2 + (p.self.fear - 0.65) / 0.35
    : 0;

  // --- flick-torch-to-listen (D-42, D-10) ---
  // D-10: listen base is character-dependent via hear.stat (P4_LISTEN_STAT_WEIGHT).
  // listenBase = P4_LISTEN_BASE + P4_LISTEN_STAT_WEIGHT * profile.hear.stat
  //   ze (0.70): 0.365 → listen 0.665 at zero fear + fresh BB → beats check (0.65)
  //   tommy (0.50): 0.275 → listen 0.575 → below check, keeps torch on
  const ticksSinceBlackboard =
    p.memory.length > 0
      ? p.tick - Math.max(...p.memory.map(e => e.createdAtTick))
      : 300;
  const listenBase = P4_LISTEN_BASE + P4_LISTEN_STAT_WEIGHT * profile.hear.stat;
  const listen =
    listenBase
    - 0.4 * Math.max(0, p.self.fear - 0.5)
    + 0.3 * Math.min(1, ticksSinceBlackboard / 300);

  // --- check-hiding-spot, patrol-base, patrol-far ---
  // Plan 06-13 (UAT Gap 5): post-dropoff patrol penalty + boost.
  // After applyDropOff sets recentDropOffTick on a hunter, patrol-base scores
  // 0 for P4_POST_DROPOFF_PATROL_BASE_PENALTY_TICKS (90 ticks ≈ 3s at 30 Hz)
  // so the hunter walks AWAY from the plinth instead of restalling at
  // map-centre.
  //
  // Rule 2 deviation (missing critical functionality): the original plan only
  // zeroed patrol-base. In practice flick-torch-to-listen scores 0.6 in the
  // post-drop-off window (no recent blackboard, fear=0), which dominates
  // patrol-far (0.30) and produces the SAME stall (StopMovement + Listen
  // leave intent.move at zero). To deliver the plan's stated truth ("hunter
  // walks AWAY"), we ALSO boost patrol-far above listen during the same
  // window — patrol-far becomes the winner and PickFarPatrolPoint produces
  // a real outer-quarter target.
  const recentDropOff = p.self.recentDropOffTick ?? null;
  const ticksSinceDropOff =
    recentDropOff != null ? p.tick - recentDropOff : Infinity;
  const inPostDropOffWindow =
    ticksSinceDropOff < P4_POST_DROPOFF_PATROL_BASE_PENALTY_TICKS;
  // Hunter-search tuning (2026-06-12, UAT "hunter isn't good at searching"):
  // check-hiding-spot now clearly out-competes patrol so the hunter actively
  // sweeps UNCHECKED hiding spots (where hiders actually are) instead of
  // drifting back toward base. When all spots are checked PickUncheckedSpot
  // FAILS and the selector falls through to patrol-far (roam) over patrol-base
  // (home).
  //
  // POL-06 / D-11 second pass (2026-06-14): widen the search bias further from
  // the first-pass values (check 0.55, patrol-base 0.22, patrol-far 0.30).
  //   - check 0.55 → 0.65: the hunter prioritises sweeping unchecked spots even
  //     more strongly (the playtest weakness was "walks past hiding spots").
  //   - patrol-base 0.22 → 0.18: even less drifting home when nothing else fires.
  //   - patrol-far 0.30 → 0.35: when every spot is checked the hunter roams the
  //     outer map to re-acquire rather than parking near base.
  // check stays comfortably below chase (1.0+) and retreat (1.2+) and above
  // patrol-far so the spot-sweep priority holds. Re-sweep decay of
  // checkedHidingSpotIds (so the hunter re-checks stale spots) is a deferred
  // lever — it lives in PickUncheckedSpot, not in these branch weights — and is
  // left for a fuller playtest pass per the hunter-search todo.
  // See .planning/todos/pending/2026-06-12-hunter-search-spotting-tuning.md.
  // --- check-hiding-spot (D-34, AI2-05) ---
  // P4_CHECKED_SPOT_PENALTY (-0.3) is now applied via checkedFraction so the check
  // utility decreases monotonically as the hunter clears more hiding spots.
  // checkedFraction = checkedHidingSpotIds.size / totalSpots, clamped [0,1].
  // At fraction=0: check = 0.65 (unchanged from previous flat value).
  // At fraction=1: check = 0.65 + (-0.3)*1 = 0.35 (all spots cleared → patrol wins).
  // The binary HasUncheckedSpots gate in the BT is NOT removed — this penalty adds
  // a gradual utility shift on top of the existing gate (belt-and-suspenders per D-07).
  // NOTE: Applying this previously-dead constant is a plan requirement; it was flagged
  // as optional in RESEARCH §Bug2 but the plan contract (AI2-05) mandates it.
  const clampedFraction = Math.min(1, Math.max(0, checkedFraction));
  const check = 0.65 + P4_CHECKED_SPOT_PENALTY * clampedFraction;
  const patrolBase = inPostDropOffWindow ? 0 : 0.18;
  const patrolFar = inPostDropOffWindow ? 1.5 : 0.35;

  return {
    chase,
    'investigate-sighting': investigate,
    'retreat-when-afraid': retreat,
    'flick-torch-to-listen': listen,
    'check-hiding-spot': check,
    'patrol-base': patrolBase,
    'patrol-far': patrolFar,
  };
}

/**
 * Score all 4 hider archetype branches given current Perception.
 * Thin wrapper over scoreHiderArchetypes from archetypes/scorers.ts.
 * TODO(Plan 10): wire caughtAtBase + totalHiders + plinth via perception extension.
 */
export function scoreHiderBranches(
  perception: Perception,
  profile: CharacterProfile,
  caughtAtBase: number,
  totalHiders: number,
  plinth: Vec2,
  recentlyFreedSuppressLiberate: boolean = false,
): Record<HiderArchetype, number> {
  return scoreHiderArchetypes(
    perception,
    profile,
    caughtAtBase,
    totalHiders,
    plinth,
    recentlyFreedSuppressLiberate,
  );
}

// ---------------------------------------------------------------------------
// Dog utility scorer (D-24, NPC-01)
// ---------------------------------------------------------------------------

/**
 * Score the 2 dog branches given current Perception.
 * bark-at-entity wins when any entity is visible (perception.visible non-empty);
 * wander is the low-score fallback.
 */
export function scoreDogBranches(p: Perception): { 'bark-at-entity': number; wander: number } {
  return {
    'bark-at-entity': p.visible.length > 0 ? 0.8 : 0,
    wander: 0.3,
  };
}

// ---------------------------------------------------------------------------
// Adult utility scorer (D-28, NPC-04)
// ---------------------------------------------------------------------------

/**
 * Score the 2 adult branches given current Perception.
 * comment-on-event wins when global cooldown elapsed AND pending events exist (D-28).
 * idle is the fallback (adults never move — NPC-02).
 */
export function scoreAdultBranches(
  p: Perception,
  lastCommentaryTick: number | null,
  pendingEvents: Array<{ eventTag: EventTag; tick: number; type: string }>,
): { 'comment-on-event': number; idle: number } {
  if (lastCommentaryTick !== null && (p.tick - lastCommentaryTick) < P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS) {
    return { 'comment-on-event': 0, idle: 0.5 };
  }
  return { 'comment-on-event': pendingEvents.length > 0 ? 1.0 : 0, idle: 0.3 };
}

/**
 * Returns the key with the highest score. Ties are broken deterministically
 * using the provided seeded RNG (D-06 per-agent sub-stream, Pitfall 10).
 *
 * @param scores   - Record of branch → score.
 * @param rng      - Per-agent seeded RNG function for tie-breaking.
 * @returns The branch id with the highest score.
 */
export function argmaxBranch<K extends string>(
  scores: Record<K, number>,
  rng: () => number,
): K {
  let bestScore = -Infinity;
  const ties: K[] = [];

  for (const k of Object.keys(scores) as K[]) {
    const s = scores[k];
    if (s > bestScore) {
      bestScore = s;
      ties.length = 0;
      ties.push(k);
    } else if (s === bestScore) {
      ties.push(k);
    }
  }

  if (ties.length === 0) return (Object.keys(scores) as K[])[0]!;
  if (ties.length === 1) return ties[0]!;
  return ties[Math.floor(rng() * ties.length)]!;
}
